// The project domain: one grading day. Holds the run list (which sessions, named, with their
// times) and the share-link registry (docs/sync.md).
//
// The registry is how a redeeming device learns what it just became: the project aggregate records
// the hash of every minted code against the role it grants and the court where the role has one
// (docs/access.md). Only the hash reaches this aggregate. The bearer key is a secret that lives on
// the admin laptop and reaches the wall as pixels, never as an event: a device that redeemed a
// coach code can read this registry, so anything in here is public to the building.
//
// The screens that write these events arrive in P2. The events, their reducers and the fold are
// here now so the aggregate definition is complete and the ordering rules have somewhere to live.

import type { Court } from './session';

export type Role = 'frontdesk' | 'coach' | 'mover' | 'admin';

export const ROLES = ['frontdesk', 'coach', 'mover', 'admin'] as const satisfies readonly Role[];

export interface ScheduledSession {
  readonly sessionId: string;
  // "under 19 boys". Sessions are scheduled ahead so the TV knows which codes to show and the
  // reports never get mixed up (docs/session-lifecycle.md).
  readonly name: string;
  readonly startsAtMs: number;
}

export interface ShareLink {
  // sha256 of the bearer key. Never the key itself.
  readonly codeHash: string;
  readonly sessionId: string;
  readonly role: Role;
  // Set for a coach code, absent for front desk, mover and admin: court identity comes from the
  // code, not from a picker (docs/access.md).
  readonly court: Court | undefined;
  readonly disabled: boolean;
}

export interface ProjectState {
  readonly sessions: readonly ScheduledSession[];
  readonly shares: readonly ShareLink[];
}

export type ProjectEvent =
  | { kind: 'sessionScheduled'; payload: { sessionId: string; name: string; startsAtMs: number } }
  | {
      kind: 'shareLinkMinted';
      payload: { codeHash: string; sessionId: string; role: Role; court?: Court };
    }
  | { kind: 'shareLinkDisabled'; payload: { codeHash: string } };

export function emptyProjectState(): ProjectState {
  return { sessions: [], shares: [] };
}

// Pure, total, deterministic, same contract as the session fold.
export function foldProjectEvent(state: ProjectState, event: ProjectEvent): ProjectState {
  switch (event.kind) {
    case 'sessionScheduled':
      return schedule(state, event.payload);
    case 'shareLinkMinted':
      return mint(state, event.payload);
    case 'shareLinkDisabled':
      return disable(state, event.payload.codeHash);
    default:
      return state;
  }
}

function schedule(state: ProjectState, session: ScheduledSession): ProjectState {
  if (state.sessions.some((s) => s.sessionId === session.sessionId)) return state;
  return {
    ...state,
    sessions: [
      ...state.sessions,
      {
        sessionId: session.sessionId,
        name: session.name,
        startsAtMs: session.startsAtMs,
      },
    ],
  };
}

function mint(
  state: ProjectState,
  minted: { codeHash: string; sessionId: string; role: Role; court?: Court },
): ProjectState {
  if (state.shares.some((s) => s.codeHash === minted.codeHash)) return state;
  return {
    ...state,
    shares: [
      ...state.shares,
      {
        codeHash: minted.codeHash,
        sessionId: minted.sessionId,
        role: minted.role,
        court: minted.court,
        disabled: false,
      },
    ],
  };
}

function disable(state: ProjectState, codeHash: string): ProjectState {
  const index = state.shares.findIndex((s) => s.codeHash === codeHash);
  if (index < 0 || state.shares[index]!.disabled) return state;
  const shares = state.shares.slice();
  shares[index] = { ...shares[index]!, disabled: true };
  return { ...state, shares };
}

// The day's run list, in the order the admin scheduled it.
export function scheduledSessions(state: ProjectState): readonly ScheduledSession[] {
  return state.sessions;
}

export function sessionById(state: ProjectState, sessionId: string): ScheduledSession | undefined {
  return state.sessions.find((s) => s.sessionId === sessionId);
}

// What a redeemed code grants, or undefined if the hash is unknown or the code was disabled. P2's
// role resolution reads this after `redeemShare` and before it opens a session handle.
export function shareForHash(state: ProjectState, codeHash: string): ShareLink | undefined {
  const share = state.shares.find((s) => s.codeHash === codeHash);
  return share === undefined || share.disabled ? undefined : share;
}

// Every live code for one session: the six the TV renders (docs/access.md).
export function sharesForSession(state: ProjectState, sessionId: string): readonly ShareLink[] {
  return state.shares.filter((s) => s.sessionId === sessionId && !s.disabled);
}
